/**
 * Authentication Layout
 * Apple-inspired clean design for login and registration
 */

import React from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Anchor, CheckCircle } from 'lucide-react';

function AuthLayout() {
  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy-900 relative overflow-hidden">
        <div className="relative z-10 flex flex-col justify-start pt-32 px-16 xl:px-20">
          {/* Logo */}
          <Link to="/" className="flex items-center mb-12">
            <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-14 w-14 rounded-xl shadow-lg" />
            <span className="ml-4 text-3xl font-bold text-white tracking-tight">
              AnchorPals
            </span>
          </Link>

          {/* Tagline */}
          <h1 className="text-5xl xl:text-6xl font-bold text-white leading-[1.05] tracking-tight">
            Your Crew at
            <br />
            Every Port.
          </h1>

          <p className="mt-6 text-lg text-navy-400 font-light leading-relaxed max-w-md">
            Connect with trusted mooring helpers worldwide. Whether you need a
            hand or want to lend one.
          </p>

          {/* Features */}
          <div className="mt-12 space-y-4">
            {[
              'Verified helpers in 500+ ports',
              'Real-time & scheduled assistance',
              'Secure in-app messaging',
              'Trusted ratings & reviews',
            ].map((feature, index) => (
              <div key={index} className="flex items-center text-navy-300 font-light">
                <CheckCircle className="h-4 w-4 text-primary-400 mr-3 flex-shrink-0" />
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col bg-white">
        {/* Mobile Logo */}
        <div className="lg:hidden px-6 py-5"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
        >
          <Link to="/" className="flex items-center">
            <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-7 w-7 rounded" />
            <span className="ml-2 text-lg font-semibold text-navy-900 tracking-tight">
              AnchorPals
            </span>
          </Link>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            <Outlet />
          </div>
        </div>

        {/* Footer */}
        <footer className="px-6 py-4 text-center text-xs text-navy-400">
          <p>&copy; 2026 AnchorPals. All rights reserved.</p>
          <div className="mt-2 space-x-6">
            <Link to="/terms" className="hover:text-navy-700 transition-colors duration-300">Terms</Link>
            <Link to="/privacy" className="hover:text-navy-700 transition-colors duration-300">Privacy</Link>
            <Link to="/help" className="hover:text-navy-700 transition-colors duration-300">Help</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default AuthLayout;
